<div class="p-4 flex flex-col gap-y-8">
  <section>
    <div class="text-lg font-bold pb-2">Driver Certifications</div>
    <mat-form-field class="w-full">
      <mat-label>Certifications</mat-label>
      <mat-select multiple [compareWith]="compareById" [formControl]="driverCertControl">
        <mat-select-trigger>
          <div class="flex items-center">
            {{ driverCertControl.value ? (certNames(driverCertControl.value) | joinWithAnd) : '' }}
          </div>
        </mat-select-trigger>
        <mat-option
          style="height: unset; line-height: 24px; font-size: 14px; white-space: normal"
          [value]="cert"
          *ngFor="let cert of certs$ | async"
        >
          <div class="flex flex-col p-1">
            <span class="font-semibold">{{ cert.name }}</span>
            <span>{{ cert.description }}</span>
          </div>
        </mat-option>
      </mat-select>
    </mat-form-field>
    <div class="flex flex-row justify-end">
      <button color="primary" (click)="updateCerts()" mat-stroked-button>Update Driver Certifications</button>
    </div>
  </section>

  <section>
    <div class="flex flex-row items-center justify-between pb-2">
      <div class="text-lg font-bold">Overweight Permits</div>
      <button
        *ngIf="editingState === null && (addableStates$ | async)?.length"
        color="primary"
        mat-stroked-button
        (click)="startAdding()"
      >
        Add Permit
      </button>
    </div>
    <p class="text-sm text-gray-600 pb-4">
      A driver with an overweight permit for a state is not docked for running above 80,000 lbs gross there, up to that
      state's maximum.
    </p>

    <div *ngIf="loadingPermits$$ | async" class="flex justify-center p-4">
      <mat-spinner diameter="32"></mat-spinner>
    </div>

    <ng-container *ngIf="(loadingPermits$$ | async) === false">
      <div *ngIf="!(permits$$ | async)?.length && editingState === null" class="text-gray-600 pb-2">
        No overweight permits recorded for this driver.
      </div>

      <div class="overflow-x-auto" *ngIf="(permits$$ | async)?.length">
        <table class="w-full text-left text-sm">
          <thead>
            <tr class="border-b">
              <th class="py-2 pr-4">State</th>
              <th class="py-2 pr-4">Max gross</th>
              <th class="py-2 pr-4">Permit #</th>
              <th class="py-2 pr-4">Effective</th>
              <th class="py-2 pr-4">Expires</th>
              <th class="py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b" *ngFor="let permit of permits$$ | async; trackBy: trackByState">
              <td class="py-2 pr-4">{{ permit.stateName }}</td>
              <td class="py-2 pr-4">{{ permit.maxGrossWeightPounds | number }} lbs</td>
              <td class="py-2 pr-4">{{ permit.permitNumber || '-' }}</td>
              <td class="py-2 pr-4">{{ permit.effectiveOn || '-' }}</td>
              <td class="py-2 pr-4">
                {{ permit.expiresOn }}
                <span *ngIf="permit.isExpired" class="ml-2 px-2 rounded bg-red-100 text-red-700 text-xs font-semibold">
                  Expired
                </span>
              </td>
              <td class="py-2 text-right whitespace-nowrap">
                <button mat-button color="primary" [disabled]="saving$$ | async" (click)="startEditing(permit)">
                  {{ permit.isExpired ? 'Renew' : 'Edit' }}
                </button>
                <button mat-button color="warn" [disabled]="saving$$ | async" (click)="removePermit(permit)">
                  {{ confirmingRemoval === permit.stateAbbreviation ? 'Confirm remove' : 'Remove' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <form
      *ngIf="editingState !== null"
      [formGroup]="permitForm"
      (ngSubmit)="savePermit()"
      class="mt-4 p-4 border rounded flex flex-col gap-y-2"
    >
      <div class="font-semibold">{{ editingState ? 'Edit permit' : 'Add permit' }}</div>

      <mat-form-field class="w-full lg:w-1/2">
        <mat-label>State</mat-label>
        <mat-select formControlName="stateAbbreviation">
          <ng-container *ngIf="editingState; else addable">
            <mat-option
              *ngFor="let limit of stateLimits$$ | async; trackBy: trackByState"
              [value]="limit.stateAbbreviation"
            >
              {{ stateLabel(limit) }}
            </mat-option>
          </ng-container>
          <ng-template #addable>
            <mat-option
              *ngFor="let limit of addableStates$ | async; trackBy: trackByState"
              [value]="limit.stateAbbreviation"
            >
              {{ stateLabel(limit) }}
            </mat-option>
          </ng-template>
        </mat-select>
        <mat-error>Choose the state the permit is for</mat-error>
      </mat-form-field>

      <div class="flex flex-col lg:flex-row gap-x-4">
        <mat-form-field class="w-full lg:w-1/4">
          <mat-label>Expires on</mat-label>
          <input
            matInput
            [matDatepicker]="expiresPicker"
            [min]="today"
            formControlName="expiresOn"
            readonly
            (click)="expiresPicker.open()"
          />
          <mat-datepicker-toggle matSuffix [for]="expiresPicker"></mat-datepicker-toggle>
          <mat-datepicker #expiresPicker></mat-datepicker>
          <mat-error>An expiry date today or later is required</mat-error>
        </mat-form-field>

        <mat-form-field class="w-full lg:w-1/4">
          <mat-label>Effective on (optional)</mat-label>
          <input
            matInput
            [matDatepicker]="effectivePicker"
            [max]="permitForm.get('expiresOn').value"
            formControlName="effectiveOn"
            readonly
            (click)="effectivePicker.open()"
          />
          <mat-datepicker-toggle matSuffix [for]="effectivePicker"></mat-datepicker-toggle>
          <mat-datepicker #effectivePicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field class="w-full lg:w-1/4">
          <mat-label>Permit number (optional)</mat-label>
          <input matInput formControlName="permitNumber" maxlength="100" />
        </mat-form-field>
      </div>

      <div class="p-3 rounded bg-yellow-50 border border-yellow-300 text-sm">
        <mat-checkbox formControlName="acknowledgedAccountability" color="primary">
          <span class="whitespace-normal">
            I confirm this driver holds a valid overweight permit for this state. Loading overweight is the driver's and
            carrier's accountability.
          </span>
        </mat-checkbox>
        <div
          *ngIf="
            permitForm.get('acknowledgedAccountability').touched && permitForm.get('acknowledgedAccountability').invalid
          "
          class="text-red-600 pt-1"
        >
          You must confirm before saving.
        </div>
      </div>

      <div class="flex flex-row justify-end gap-x-2 pt-2">
        <button type="button" mat-button [disabled]="saving$$ | async" (click)="cancelEditing()">Cancel</button>
        <button type="submit" mat-flat-button color="primary" [disabled]="saving$$ | async">Save Permit</button>
      </div>
    </form>
  </section>
</div>
